/* Optional product contact email. Does not change price, discount or marketing consent. */
(function () {
  'use strict';
  if (window.LPProductEmail) { window.LPProductEmail.init(); return; }
  const PROPERTY = '_Contact email';
  const EXCLUDED = 'quick-add-modal, .mw-tingle, product-recommendations, .product-card-wrapper, .complementary-products';
  const STORAGE = 'lpProductContactEmail';
  const EMAIL_ERROR = 'Please enter a valid email address (e.g. name@example.com), or leave this field blank.';
  const DOMAIN_ERROR = 'This email domain cannot receive mail. Please check the address, or leave this field blank.';
  const DOMAIN_UNKNOWN = 'We could not check this email domain. Please try again, or leave this field blank.';
  const domainCache = new Map();
  const domainQueries = new Map();
  const resumedActions = new WeakSet();
  const instances = new Map();
  const drafts = new Map();
  const resumed = new WeakSet();
  const pendingCartForms = new WeakSet();
  const sent = new Set();
  let helpDialog;
  let helpOpener;
  let latest = null;
  try { latest = JSON.parse(sessionStorage.getItem(STORAGE) || 'null'); } catch (_) {}
  function email(value) {
    const result = String(value || '').trim();
    if (!result || result.length > 254) return '';
    const parts = result.split('@');
    if (parts.length !== 2) return '';
    const [local, domain] = parts;
    if (!local || local.length > 64 || !/^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+$/.test(local) ||
        local.startsWith('.') || local.endsWith('.') || local.includes('..')) return '';
    const labels = domain.split('.');
    if (labels.length < 2 || labels.some(label => !/^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(label))) return '';
    const suffix = labels.at(-1);
    if (!/^[a-zA-Z]{2,63}$/.test(suffix) && !/^xn--[a-zA-Z0-9-]+[a-zA-Z0-9]$/i.test(suffix)) return '';
    return result;
  }
  function endpoint(value) {
    // Only an explicitly configured, same-store Shopify app proxy is permitted.
    return /^\/apps\/[a-zA-Z0-9_/-]+$/.test(value || '') ? value : '';
  }
  function controller(scope) {
    if (scope?.closest?.(EXCLUDED)) return null;
    const root = scope?.closest?.('[id^="MainProduct-"]');
    return root && instances.get(root);
  }
  function valueFor(scope) { return controller(scope)?.input.value.trim() || ''; }
  function error(instance, text) {
    instance.message.textContent = text;
    instance.message.hidden = !text;
  }
  function domainFor(instance) { return email(instance.input.value).split('@')[1]?.toLowerCase() || ''; }
  function domainResult(instance) {
    const domain = domainFor(instance);
    const saved = domainCache.get(domain);
    if (saved?.expires > Date.now()) return saved;
    const result = instance.domainFailure;
    return result?.domain === domain && (result.status === 'unknown' || result.expires > Date.now()) ? result : null;
  }
  function domainError(instance) {
    const domain = domainFor(instance);
    const suggestion = { 'qq.coa': 'qq.com', 'qq.con': 'qq.com', 'gmail.con': 'gmail.com', 'outlook.con': 'outlook.com', 'hotmail.con': 'hotmail.com' }[domain];
    return suggestion ? 'Please check the email domain. Did you mean ' + suggestion + '? Correct it, or leave this field blank.' : DOMAIN_ERROR;
  }
  function validate(instance, focus = false) {
    instance.touched = true;
    const value = instance.input.value.trim();
    const formatValid = !value || !!email(value);
    const result = value && formatValid ? domainResult(instance) : null;
    const checking = !!value && instance.checkingDomain === domainFor(instance);
    const text = !formatValid ? EMAIL_ERROR : result?.status === 'invalid' ? domainError(instance) :
      result?.status === 'unknown' ? DOMAIN_UNKNOWN : checking ? 'Checking email domain\u2026' : '';
    const valid = formatValid && !text;
    instance.input.setAttribute('aria-invalid', String(!formatValid || result?.status === 'invalid'));
    instance.input.setAttribute('aria-busy', String(checking));
    instance.input.setCustomValidity(text);
    // Blur occurs between pointer-down and click. A loading line at that point
    // would move the purchase button out from under the pointer and lose it.
    error(instance, checking && !instance.pendingAction ? '' : text);
    if (!valid && focus) instance.input.focus();
    return valid;
  }
  async function ensureDomain(instance) {
    const domain = domainFor(instance);
    if (!domain) return !instance.input.value.trim();
    const saved = domainResult(instance);
    if (saved && saved.status !== 'unknown') return saved.status === 'valid';
    instance.domainFailure = null; instance.checkingDomain = domain;
    validate(instance);
    const capturePath = endpoint(instance.config.captureEndpoint);
    const target = capturePath?.endsWith('/email') ? capturePath.slice(0, -6) + '/validate-domain' : '/apps/lp-contact/validate-domain';
    const key = target + '|' + domain;
    let query = domainQueries.get(key);
    if (!query) {
      query = (async () => {
        try {
          const response = await fetch(target, {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            signal: AbortSignal.timeout(12000), body: JSON.stringify({ domain })
          });
          const data = await response.json();
          if (!response.ok || data.domain !== domain || !['valid', 'invalid', 'unknown'].includes(data.status)) throw new Error('Domain lookup unavailable');
          const result = { domain, status: data.status, expires: data.status === 'unknown' ? 0 : Date.now() + Math.max(1, Math.min(300, Number(data.ttl) || 1)) * 1000 };
          if (result.status !== 'unknown' && result.expires > Date.now()) {
            if (domainCache.size >= 100) domainCache.delete(domainCache.keys().next().value);
            domainCache.set(domain, result);
          }
          return result;
        } catch (_) { return { domain, status: 'unknown', expires: 0 }; }
      })().finally(() => domainQueries.delete(key));
      domainQueries.set(key, query);
    }
    const result = await query;
    if (domainFor(instance) !== domain) return false;
    if (instance.checkingDomain === domain) instance.checkingDomain = null;
    // Keep uncached/unknown results long enough to show a retry message.
    instance.domainFailure = result;
    validate(instance); sync(instance);
    return result.status === 'valid';
  }
  function forms(instance) {
    return [...instance.root.querySelectorAll('form[action*="/cart/add"]')].filter(form => {
      const productId = form.querySelector('[name="product-id"]')?.value;
      return !form.closest(EXCLUDED) && (!productId || String(productId) === String(instance.config.productId));
    });
  }
  function sync(instance) {
    const value = domainResult(instance)?.status === 'invalid' ? '' : email(instance.input.value);
    forms(instance).forEach(form => {
      let field = form.querySelector('[data-lp-email-property]');
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden'; field.name = 'properties[' + PROPERTY + ']';
        field.dataset.lpEmailProperty = ''; form.append(field);
      }
      field.value = value; field.disabled = !value;
    });
  }
  function prepare(scope, formData) {
    const instance = controller(scope);
    if (!instance) return true;
    const value = instance.input.value.trim();
    const result = domainResult(instance);
    if (email(value) && result?.status !== 'valid' && result?.status !== 'invalid') {
      void ensureDomain(instance); validate(instance, true); return false;
    }
    if (!validate(instance, true)) return false;
    sync(instance);
    if (formData) {
      if (value) formData.set('properties[' + PROPERTY + ']', value);
      else formData.delete('properties[' + PROPERTY + ']');
    }
    return true;
  }
  function visible(node) {
    return !!node && !node.closest('[hidden], [aria-hidden="true"], ' + EXCLUDED) && !!node.getClientRects().length;
  }
  function showHelp(button) {
    helpOpener = button;
    if (!helpDialog) {
      helpDialog = document.createElement('dialog');
      helpDialog.className = 'lp-product-email-dialog';
      helpDialog.setAttribute('aria-labelledby', 'LPContactEmailHelpTitle');
      helpDialog.setAttribute('aria-describedby', 'LPContactEmailHelpText');
      const close = document.createElement('button');
      close.type = 'button'; close.className = 'lp-product-email-dialog__close';
      close.setAttribute('aria-label', 'Close'); close.textContent = '\u00d7';
      close.addEventListener('click', () => helpDialog.close());
      const title = document.createElement('h2');
      title.id = 'LPContactEmailHelpTitle'; title.textContent = 'Free Preview & Revisions';
      const text = document.createElement('p');
      text.id = 'LPContactEmailHelpText'; text.textContent = 'Preview arrives in 1\u20132 business days. Changes are free.';
      helpDialog.append(close, title, text);
      helpDialog.addEventListener('close', () => { if (helpOpener?.isConnected && visible(helpOpener)) helpOpener.focus(); });
      helpDialog.addEventListener('click', event => {
        if (event.target !== helpDialog) return;
        const bounds = helpDialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) helpDialog.close();
      });
      document.body.append(helpDialog);
    }
    if (!helpDialog.open) helpDialog.showModal();
  }
  function place(instance) {
    const { info, widget, config } = instance;
    sync(instance);
    if (config.giftLayout) {
      const upload = [...info.querySelectorAll('input[type="file"]')]
        .map(input => input.closest('.mw-option') || input.closest('[data-upload-area]'))
        .filter(node => node && !node.closest(EXCLUDED) && visible(node)).at(-1);
      if (upload) {
        if (widget.previousElementSibling !== upload) upload.after(widget);
        widget.hidden = false; return;
      }
    }
    const prices = [...info.querySelectorAll('[data-lp-price-under-title], .price-wrapp')].filter(node => !node.closest(EXCLUDED));
    const price = prices.find(node => node.matches('.lp-price-before-apo-next, .lp-gift-price-before-buy')) || prices[0] ||
      [...info.querySelectorAll('.price')].find(node => !node.closest(EXCLUDED + ', .mw-option'));
    const hasApo = [...info.querySelectorAll('[id*="__apo_product_options_app_block_"], .mw-option')].some(node => !node.closest(EXCLUDED));
    const row = [...info.querySelectorAll(hasApo ? '.mw-group__button, .mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned' : '.product-form__buttons')].find(visible);
    if (instance.portrait && hasApo) {
      const container = row?.closest('.mw-group__container') ||
        [...info.querySelectorAll('.mw-group__container')].find(node => !node.closest(EXCLUDED));
      // APO keeps all step groups mounted, and its hidden groups can still
      // have layout boxes. Use its step class rather than geometry alone.
      const groups = container ? [...container.children].filter(node => node.classList.contains('mw-group')) : [];
      const active = groups.filter(node => !node.classList.contains('mw-group__hidden') &&
        !node.hidden && node.getAttribute('aria-hidden') !== 'true');
      if (!container || !active.length || active.includes(groups[0])) {
        widget.hidden = true;
        if (helpDialog?.open && widget.contains(helpOpener)) helpDialog.close();
        return;
      }
    }
    // A hidden native price can remain above APO's stepper. Never anchor the
    // contact field there: follow the active step's purchase area instead.
    let anchor = info.querySelector('[data-lp-price-under-title]') ? row : hasApo ? row : price || row;
    if (hasApo && row) {
      // Walk the whole pricing cluster, including the standalone discount widget.
      // Inserting between price and discount can break existing adjacency CSS.
      let previous = row.previousElementSibling;
      while (previous) {
        if (previous === widget) { previous = previous.previousElementSibling; continue; }
        if (!previous.matches('.price-wrapp, .price, [data-lp-discount-widget], [data-lp-mobile-initial-price], script, style')) break;
        anchor = previous; previous = previous.previousElementSibling;
      }
    }
    const summary = info.querySelector('[data-lp-mobile-initial-price]');
    // Leave the mobile summary / price / purchase-row adjacency intact.
    if (summary && anchor && summary.parentElement === anchor.parentElement && summary.nextElementSibling === anchor) anchor = summary;
    if (anchor) {
      if (widget.nextElementSibling !== anchor) anchor.before(widget);
      widget.hidden = false;
    } else widget.hidden = true;
  }
  function init() {
    document.querySelectorAll('[data-lp-product-email-config]').forEach(node => {
      let config;
      try { config = JSON.parse(node.textContent); } catch (_) { return; }
      const root = document.getElementById('MainProduct-' + config.sectionId);
      const info = root?.querySelector('.product__info-container');
      if (!info || root.closest(EXCLUDED)) return;
      const previous = instances.get(root);
      if (previous) { previous.schedule(); return; }
      const widget = document.createElement('div');
      widget.dataset.lpProductEmail = ''; widget.hidden = true;
      const id = 'LPContactEmail-' + config.sectionId;
      const portrait = !config.giftLayout && ['2d', '3d'].includes(root.dataset.lpBenefitsType);
      const label = '<label for="' + id + '">Email <span>(optional)</span></label>';
      widget.innerHTML = (portrait ? '<div class="lp-product-email__label">' + label +
        '<button type="button" class="lp-product-email__help" aria-label="About free previews and revisions" aria-haspopup="dialog"><span aria-hidden="true">?</span></button></div>' : label) +
        '<input id="' + id + '" type="email" autocomplete="email" inputmode="email" maxlength="254" aria-describedby="' + id + '-message" aria-invalid="false">' +
        '<p id="' + id + '-message" class="lp-product-email__message" role="status" aria-live="polite" hidden></p>';
      const instance = { root, info, config, portrait, widget, input: widget.querySelector('input'), message: widget.querySelector('p'), touched: false, checkingDomain: null, domainFailure: null, pendingAction: false, frame: null };
      widget.querySelector('.lp-product-email__help')?.addEventListener('click', event => {
        event.preventDefault(); event.stopPropagation(); showHelp(event.currentTarget);
      });
      instance.input.value = drafts.get(String(config.productId)) || '';
      instance.schedule = () => {
        if (instance.frame !== null) return;
        instance.frame = requestAnimationFrame(() => { instance.frame = null; place(instance); });
      };
      instance.input.addEventListener('input', () => {
        drafts.set(String(config.productId), instance.input.value);
        instance.domainFailure = null;
        if (instance.touched) validate(instance);
        sync(instance);
      });
      instance.input.addEventListener('blur', () => {
        instance.input.value = instance.input.value.trim();
        drafts.set(String(config.productId), instance.input.value);
        validate(instance); sync(instance);
        if (email(instance.input.value)) void ensureDomain(instance);
      });
      instance.input.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); prepare(instance.input); }
      });
      instance.observer = new MutationObserver(changes => {
        if (changes.some(change => !widget.contains(change.target))) instance.schedule();
      });
      instance.observer.observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'hidden', 'aria-hidden', 'style'] });
      instances.set(root, instance); place(instance);
    });
  }
  function itemsFrom(response) { return Array.isArray(response?.items) ? response.items : response?.variant_id || response?.id ? [response] : []; }
  function remember(value) {
    latest = value;
    try { value ? sessionStorage.setItem(STORAGE, JSON.stringify(value)) : sessionStorage.removeItem(STORAGE); } catch (_) {}
  }
  async function capture(instance, response, value, method) {
    const target = endpoint(instance.config.captureEndpoint);
    if (!target) return; // No app proxy has been installed: do not claim a customer was created.
    const key = [value.toLowerCase(), instance.config.productId, method].join('|');
    if (sent.has(key)) return;
    sent.add(key);
    try {
      const result = await fetch(target, {
        method: 'POST', credentials: 'same-origin', keepalive: true,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        signal: AbortSignal.timeout(4000),
        body: JSON.stringify({ email: value, productId: String(instance.config.productId),
          variantIds: itemsFrom(response).map(item => String(item.variant_id || item.id)), source: method,
          requestId: crypto.randomUUID(), marketingOptIn: false })
      });
      const data = await result.json();
      if (!result.ok || data.success !== true) throw new Error('Contact record was not confirmed.');
      document.dispatchEvent(new CustomEvent('lp:product-email-captured', { detail: { productId: instance.config.productId } }));
    } catch (_) {
      sent.delete(key);
      document.dispatchEvent(new CustomEvent('lp:product-email-capture-error', { detail: { productId: instance.config.productId } }));
    }
  }
  function confirm(scope, response, method = 'cart', submittedEmail) {
    const instance = controller(scope);
    if (!instance) return;
    const value = submittedEmail === undefined ? email(instance.input.value) : email(submittedEmail);
    const items = itemsFrom(response);
    if (!value) { if (items.length) remember(null); return; }
    if (!items.some(item => email(item.properties?.[PROPERTY]) === value)) return;
    remember({ email: value, at: Date.now() });
    // Customer capture is optional and independent of successful cart/order persistence.
    void capture(instance, response, value, method);
  }
  async function readCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(5000) });
    if (!response.ok) throw new Error('Cart unavailable');
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
    return cart;
  }
  async function complete(scope, response, submittedEmail, method) {
    const instance = controller(scope);
    const value = email(submittedEmail);
    if (!instance || !response || !value) { confirm(scope, response, method, submittedEmail); return response; }
    const items = itemsFrom(response);
    if (items.some(item => email(item.properties?.[PROPERTY]) === value)) {
      confirm(scope, response, method, value); return response;
    }
    // Some APO writers build their own payload. Merge only into the confirmed
    // main-product line; never replace photo/options properties or guess by variant alone.
    const candidate = items.find(item => String(item.product_id) === String(instance.config.productId)) ||
      (items.length === 1 ? items[0] : null);
    if (!candidate?.key) return response;
    try {
      const cart = await readCart();
      const line = cart.items.find(item => item.key === candidate.key && String(item.product_id) === String(instance.config.productId));
      if (!line) return response;
      const properties = { ...line.properties, [PROPERTY]: value };
      const root = window.Shopify?.routes?.root || '/';
      const result = await fetch(root + 'cart/change.js', {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(8000), body: JSON.stringify({ id: line.key, quantity: line.quantity, properties })
      });
      const updated = await result.json();
      if (!result.ok) throw new Error('Email save failed');
      const saved = updated.items?.find(item => String(item.variant_id) === String(line.variant_id) &&
        Object.entries(properties).every(([key, entry]) => item.properties?.[key] === entry));
      if (!saved) throw new Error('Email save failed');
      const patched = Array.isArray(response.items) ? { ...response, items: items.map(item => item.key === candidate.key ? saved : item) } : { ...response, ...saved };
      confirm(scope, patched, method, value); return patched;
    } catch (_) {
      error(instance, 'Your item was added, but the contact email could not be saved. Please enter it at checkout.');
      return response;
    }
  }
  function checkoutURL(url, value) {
    const target = new URL(url, window.location.origin);
    const contact = email(value);
    if (target.origin === window.location.origin && /\/checkout\/?$/.test(target.pathname) && contact) target.searchParams.set('checkout[email]', contact);
    return target;
  }
  function resume(form, submitter) {
    resumed.add(form);
    if (submitter?.isConnected && submitter.form === form) form.requestSubmit(submitter);
    else form.requestSubmit();
  }
  async function cartCheckout(event, form, submitter) {
    event.preventDefault();
    if (pendingCartForms.has(form)) return;
    pendingCartForms.add(form);
    try {
      const cart = await readCart();
      const contact = email(latest?.email);
      if (!cart.items.some(item => email(item.properties?.[PROPERTY]) === contact)) { remember(null); resume(form, submitter); return; }
      // Preserve unsaved cart quantities by letting their original submit continue.
      const updates = [...form.elements].filter(field => field.name === 'updates[]');
      const customFields = [...form.elements].some(field => field.name && !field.disabled &&
        !['button', 'submit', 'reset'].includes(field.type) &&
        (!['radio', 'checkbox'].includes(field.type) || field.checked) &&
        !['checkout', 'note', 'updates[]', 'form_type', 'utf8'].includes(field.name));
      // App-added attributes and alternate update formats keep their native submission.
      if (customFields) { resume(form, submitter); return; }
      if (updates.length && (updates.length !== cart.items.length || updates.some((field, index) => Number(field.value) !== Number(cart.items[index].quantity)))) {
        resume(form, submitter); return;
      }
      const note = [...form.elements].find(field => field.name === 'note');
      if (note && note.value !== (cart.note || '')) {
        const result = await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(5000), body: JSON.stringify({ note: note.value })
        });
        if (!result.ok) throw new Error('Note save failed');
      }
      window.location.assign(checkoutURL((window.Shopify?.routes?.root || '/') + 'checkout', contact).href);
    } catch (_) { resume(form, submitter); }
    finally { pendingCartForms.delete(form); }
  }
  window.LPProductEmail = { init, prepare, valueFor, confirm, complete, checkoutURL,
    isResumedAction: target => resumedActions.has(target) };
  function stepState(instance) {
    return [...instance.info.querySelectorAll('.mw-group__container > .mw-group')]
      .map(node => String(!node.classList.contains('mw-group__hidden'))).join('|');
  }
  function awaitDomainAction(event, instance, continueAction) {
    event.preventDefault(); event.stopImmediatePropagation();
    if (instance.pendingAction) return;
    const value = instance.input.value.trim();
    const step = stepState(instance);
    instance.pendingAction = true;
    void ensureDomain(instance).then(valid => {
      if (valid && instance.root.isConnected && instance.input.value.trim() === value && stepState(instance) === step) continueAction();
      else if (instance.input.value.trim() === value && instance.root.isConnected) validate(instance, true);
    }).finally(() => { instance.pendingAction = false; });
  }
  function validateAction(event) {
    const target = event.target.closest?.('.mw-group__button-next, .mw-group__button-next--addtocart, .mw-apo-add-to-cart-custom, .mw-apo-add-to-cart-cloned, [data-checkout-method], button[name="add"], shopify-accelerated-checkout, .shopify-payment-button');
    const instance = controller(target);
    if (!target || target.closest(EXCLUDED) || !instance) return;
    // Returning from a later step keeps the draft. Let the hidden first-step
    // Next button reach the email step, where the user can correct that draft.
    const nextStep = target.matches('.mw-group__button-next') &&
      !target.matches('.mw-group__button-next--addtocart, .mw-group__button-next--buy-now');
    if (nextStep && instance.widget.hidden) return;
    const value = instance.input.value.trim();
    const result = domainResult(instance);
    if (value && email(value) && result?.status !== 'valid' && result?.status !== 'invalid') {
      awaitDomainAction(event, instance, () => {
        if (!target.isConnected) return;
        // Native accelerated wallets can require a trusted user gesture. Let a
        // fresh click reach them after the automatic lookup, without synthetic payment.
        if (target.matches('shopify-accelerated-checkout, .shopify-payment-button')) {
          error(instance, 'Email domain checked. Please click your payment option again.');
        } else {
          resumedActions.add(target);
          try { target.click(); } finally { resumedActions.delete(target); }
        }
      });
      return;
    }
    if (!prepare(target)) { event.preventDefault(); event.stopImmediatePropagation(); }
  }
  window.addEventListener('click', validateAction, true);
  window.addEventListener('keydown', event => {
    if (event.key === 'Enter' || event.key === ' ') validateAction(event);
  }, true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (resumed.has(form)) { resumed.delete(form); return; }
    const instance = controller(form);
    if (instance && !form.closest(EXCLUDED)) {
      const value = instance.input.value.trim();
      const result = domainResult(instance);
      if (value && email(value) && result?.status !== 'valid' && result?.status !== 'invalid') {
        awaitDomainAction(event, instance, () => {
          if (form.isConnected && prepare(form)) resume(form, event.submitter);
        });
        return;
      }
      if (!prepare(form)) { event.preventDefault(); event.stopImmediatePropagation(); }
      return;
    }
    if (event.defaultPrevented || !email(latest?.email) || Date.now() - Number(latest.at) > 86400000) return;
    const submitter = event.submitter;
    const action = new URL(form.action, window.location.origin);
    if (action.origin !== window.location.origin || !/\/cart\/?$/.test(action.pathname) || submitter?.name !== 'checkout') return;
    if (!form.closest('cart-drawer, cart-items, [id^="shopify-section-"]')) return;
    void cartCheckout(event, form, submitter);
  }, true);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => {
      if (event.target.contains(root)) {
        if (helpDialog?.open && root.contains(helpOpener)) helpDialog.close();
        drafts.set(String(instance.config.productId), instance.input.value);
        instance.observer.disconnect(); cancelAnimationFrame(instance.frame); instances.delete(root);
      }
    });
  });
  window.addEventListener('pageshow', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
